/**
 * WHAT A FAILED ROLLUP ACTION SAYS ON SCREEN.
 *
 * `ApiCallError.message` is the machine CODE — its constructor takes
 * `payload.error` — so `e instanceof Error ? e.message : …` puts things like
 * "Failed / imported_rollup" in front of the PM. That is what Reopen on an
 * imported week actually showed.
 *
 * Every refusal these endpoints raise already carries prose in the response
 * body's `message`, written for exactly this moment. Prefer it; fall back to
 * the caller's sentence, never to the code.
 *
 * Deliberately NOT a code→copy map like `submissionErrorMessage`. That one
 * exists because the submissions routes predate the habit of returning a
 * message and their copy has to live client-side. Adding a second map here
 * would mean maintaining the same sentence in two repos-worth of places; the
 * server already said it.
 */
import { ApiCallError } from './api';

export function rollupActionErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiCallError) {
    // A SYNTHESIZED payload is not a server sentence. `readErrorPayload`
    // (lib/api.ts) keeps a non-JSON body — a Cloudflare edge interstitial, a
    // proxy's complaint — as `message`, under a made-up `http_<status>` code.
    // Preferring that would toast raw HTML at the PM. A real refusal from our
    // own API always names its own code, so the code IS the discriminator.
    if (/^http_\d+$/.test(String(err.payload.error ?? ''))) return fallback;
    // The server's own sentence, when it sent one.
    const fromBody = (err.payload as { message?: unknown }).message;
    if (typeof fromBody === 'string' && fromBody.trim().length > 0) return fromBody;
    // Timeout and network failures carry prose on `message` instead (the
    // constructor's code-as-message only applies to real HTTP responses).
    if (err.kind !== 'http') return err.message;
    return fallback;
  }
  // A non-API error is something thrown client-side; its message is prose.
  if (err instanceof Error && err.message.trim().length > 0) return err.message;
  return fallback;
}
